import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { TablerIconsModule } from 'angular-tabler-icons';
import { Subscription } from 'rxjs';
import { JetsonStatusService, JetsonStatus, ConnectionState } from 'src/app/services/jetson-status.service';
import { EngineRestartButtonComponent } from 'src/app/components/engine-restart-button/engine-restart-button.component';

@Component({
  selector: 'app-jetson-status-indicator',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatButtonModule, MatMenuModule, MatDividerModule, TablerIconsModule, EngineRestartButtonComponent],
  template: `
    <!-- Chip de estado visible -->
    <button class="status-chip" [ngClass]="connectionState"
            [matMenuTriggerFor]="statusPanel"
            [matTooltip]="tooltipText"
            matTooltipPosition="below">
      <!-- Dot animado -->
      <span class="live-dot">
        <span class="dot-core"></span>
        <span class="dot-ring" *ngIf="connectionState === 'online'"></span>
      </span>
      <!-- Icono -->
      <i-tabler [name]="stateIcon" class="chip-icon"></i-tabler>
      <!-- Texto -->
      <span class="chip-label">{{ statusText }}</span>
    </button>

    <!-- Panel desplegable con detalles -->
    <mat-menu #statusPanel="matMenu" xPosition="before" class="status-panel cardWithShadow">
      <div class="panel-header" (click)="$event.stopPropagation()">
        <div class="d-flex align-items-center gap-12">
          <div class="header-icon" [ngClass]="connectionState">
            <i-tabler [name]="stateIcon" class="icon-20"></i-tabler>
          </div>
          <div>
            <h6 class="f-s-14 f-w-600 m-0">{{ statusTitle }}</h6>
            <span class="f-s-12 text-body-secondary">{{ lastSeenLabel }}</span>
          </div>
        </div>
      </div>

      <mat-divider></mat-divider>

      <div class="panel-body" (click)="$event.stopPropagation()">
        @if (status && status.devices.length > 0) {
          @for (device of status.devices; track device.hardwareId) {
            <div class="device-row">
              <span class="device-dot" [class.online]="device.online" [class.offline]="!device.online"></span>
              <div class="device-info">
                <span class="f-s-13 f-w-500">{{ device.deviceName || 'Dispositivo' }}</span>
                <span class="f-s-11 text-body-secondary">{{ device.online ? 'Conectado' : 'Desconectado' }}</span>
              </div>
              <span class="device-badge" [class.online]="device.online" [class.offline]="!device.online">
                {{ device.online ? 'ON' : 'OFF' }}
              </span>
            </div>
          }
        } @else {
          <div class="empty-state">
            <i-tabler name="device-desktop-off" class="icon-28 text-body-secondary"></i-tabler>
            <span class="f-s-13 text-body-secondary m-t-4">Sin controladores registrados</span>
          </div>
        }
      </div>

      <mat-divider></mat-divider>

      <div class="panel-actions" (click)="$event.stopPropagation()">
        <span class="f-s-11 f-w-600 text-body-secondary m-b-8 d-block">Acciones</span>
        <app-engine-restart-button></app-engine-restart-button>
      </div>
    </mat-menu>
  `,
  styles: [`
    /* ========================================
       STATUS CHIP - Elemento principal visible
       ======================================== */
    .status-chip {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 14px 6px 10px;
      border-radius: 20px;
      border: 1.5px solid transparent;
      cursor: pointer;
      font-family: inherit;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.01em;
      white-space: nowrap;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      outline: none;
      position: relative;
    }

    /* --- ONLINE: fondo verde tenue, borde y texto verde --- */
    .status-chip.online {
      background: rgba(34, 197, 94, 0.1);
      border-color: rgba(34, 197, 94, 0.35);
      color: #16a34a;
    }
    .status-chip.online:hover {
      background: rgba(34, 197, 94, 0.18);
      border-color: rgba(34, 197, 94, 0.5);
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
    }

    /* --- PARTIAL: naranja --- */
    .status-chip.partial {
      background: rgba(245, 158, 11, 0.1);
      border-color: rgba(245, 158, 11, 0.4);
      color: #b45309;
      animation: chip-attention 3s ease-in-out infinite;
    }
    .status-chip.partial:hover {
      background: rgba(245, 158, 11, 0.18);
      border-color: rgba(245, 158, 11, 0.55);
      box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
    }

    /* --- RECONNECTING: amarillo/naranja con pulso más rápido que partial --- */
    .status-chip.reconnecting {
      background: rgba(245, 158, 11, 0.1);
      border-color: rgba(245, 158, 11, 0.4);
      color: #b45309;
      animation: chip-reconnecting 1.5s ease-in-out infinite;
    }
    .status-chip.reconnecting:hover {
      background: rgba(245, 158, 11, 0.18);
      border-color: rgba(245, 158, 11, 0.55);
      box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
    }

    /* --- OFFLINE: rojo, llama la atencion --- */
    .status-chip.offline {
      background: rgba(239, 68, 68, 0.1);
      border-color: rgba(239, 68, 68, 0.4);
      color: #dc2626;
      animation: chip-alert 2s ease-in-out infinite;
    }
    .status-chip.offline:hover {
      background: rgba(239, 68, 68, 0.18);
      border-color: rgba(239, 68, 68, 0.55);
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
    }

    /* --- UNKNOWN: gris neutro --- */
    .status-chip.unknown {
      background: rgba(107, 114, 128, 0.08);
      border-color: rgba(107, 114, 128, 0.25);
      color: #6b7280;
    }
    .status-chip.unknown:hover {
      background: rgba(107, 114, 128, 0.14);
      border-color: rgba(107, 114, 128, 0.4);
    }

    /* ========================================
       LIVE DOT - Punto animado de estado
       ======================================== */
    .live-dot {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 12px;
      height: 12px;
      flex-shrink: 0;
    }

    .dot-core {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: currentColor;
      position: relative;
      z-index: 1;
    }

    /* Anillo pulsante solo en online */
    .dot-ring {
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      border: 2px solid currentColor;
      opacity: 0;
      animation: ring-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes ring-pulse {
      0% {
        transform: scale(0.8);
        opacity: 0.6;
      }
      70% {
        transform: scale(1.8);
        opacity: 0;
      }
      100% {
        transform: scale(1.8);
        opacity: 0;
      }
    }

    /* ========================================
       CHIP ICON e LABEL
       ======================================== */
    .chip-icon {
      width: 16px;
      height: 16px;
      display: flex;
      flex-shrink: 0;
    }

    .chip-label {
      line-height: 1;
    }

    /* ========================================
       ANIMACIONES de atencion para estados criticos
       ======================================== */
    @keyframes chip-alert {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
      }
      50% {
        box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15);
      }
    }

    @keyframes chip-attention {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
      }
      50% {
        box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.12);
      }
    }

    @keyframes chip-reconnecting {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
        opacity: 1;
      }
      50% {
        box-shadow: 0 0 0 5px rgba(245, 158, 11, 0.2);
        opacity: 0.85;
      }
    }

    /* ========================================
       PANEL desplegable
       ======================================== */
    .panel-header {
      padding: 16px 20px;
    }

    .header-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      flex-shrink: 0;
    }

    .header-icon.online {
      background-color: rgba(34, 197, 94, 0.12);
      color: #16a34a;
    }

    .header-icon.partial {
      background-color: rgba(245, 158, 11, 0.12);
      color: #d97706;
    }

    .header-icon.reconnecting {
      background-color: rgba(245, 158, 11, 0.12);
      color: #d97706;
    }

    .header-icon.offline {
      background-color: rgba(239, 68, 68, 0.12);
      color: #dc2626;
    }

    .header-icon.unknown {
      background-color: rgba(156, 163, 175, 0.12);
      color: #6b7280;
    }

    .panel-body {
      padding: 8px 12px 12px;
      max-height: 240px;
      overflow-y: auto;
    }

    .device-row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 10px;
      border-radius: 8px;
      transition: background-color 0.15s ease;
    }

    .device-row:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }

    .device-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .device-dot.online {
      background-color: #22c55e;
    }

    .device-dot.offline {
      background-color: #ef4444;
    }

    .device-info {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
      line-height: 1.3;
    }

    .device-badge {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.5px;
      padding: 2px 8px;
      border-radius: 4px;
      flex-shrink: 0;
    }

    .device-badge.online {
      background-color: rgba(34, 197, 94, 0.12);
      color: #16a34a;
    }

    .device-badge.offline {
      background-color: rgba(239, 68, 68, 0.1);
      color: #dc2626;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 20px 16px;
      gap: 4px;
    }

    .panel-actions {
      padding: 12px 16px 14px;
      display: flex;
      flex-direction: column;
    }
  `]
})
export class JetsonStatusIndicatorComponent implements OnInit, OnDestroy {
  private statusSubscription?: Subscription;

  status: JetsonStatus | null = null;
  connectionState: ConnectionState = 'unknown';
  tooltipText = 'Verificando estado del controlador...';
  statusText = 'Verificando...';
  statusTitle = 'Controladores';
  lastSeenLabel = 'Verificando...';
  stateIcon = 'cpu';

  constructor(private jetsonStatusService: JetsonStatusService) { }

  ngOnInit(): void {
    this.jetsonStatusService.startPolling();

    this.statusSubscription = this.jetsonStatusService.status$.subscribe(status => {
      this.status = status;
      this.connectionState = this.jetsonStatusService.getConnectionState(status);
      this.updateTexts();
    });
  }

  ngOnDestroy(): void {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
  }

  private updateTexts(): void {
    const lastSeenText = this.jetsonStatusService.getLastSeenText(this.status);

    switch (this.connectionState) {
      case 'online':
        this.stateIcon = 'wifi';
        this.statusText = this.status && this.status.deviceCount > 1
          ? `${this.status.deviceCount} Online`
          : 'Conectado';
        this.statusTitle = this.status && this.status.deviceCount > 1
          ? `${this.status.deviceCount} controladores conectados`
          : 'Controlador conectado';
        this.lastSeenLabel = lastSeenText;
        this.tooltipText = 'Controlador conectado - clic para ver detalles';
        break;
      case 'reconnecting':
        this.stateIcon = 'refresh';
        this.statusText = 'Reconectando...';
        this.statusTitle = 'Reconectando cámara';
        this.lastSeenLabel = 'Reintentando conexión RTSP';
        this.tooltipText = 'Reconectando cámara - clic para ver detalles';
        break;
      case 'partial':
        this.stateIcon = 'alert-triangle';
        this.statusText = `${this.status?.onlineCount}/${this.status?.deviceCount} Online`;
        this.statusTitle = `${this.status?.onlineCount} de ${this.status?.deviceCount} online`;
        this.lastSeenLabel = 'Algunos controladores desconectados';
        this.tooltipText = 'Conexion parcial - clic para ver detalles';
        break;
      case 'offline':
        this.stateIcon = 'wifi-off';
        this.statusText = 'Desconectado';
        this.statusTitle = 'Controlador desconectado';
        this.lastSeenLabel = `Ultima conexion: ${lastSeenText}`;
        this.tooltipText = 'Sin conexion al controlador';
        break;
      case 'unknown':
      default:
        this.stateIcon = 'cpu-off';
        this.statusText = 'Sin Jetson';
        this.statusTitle = 'Sin controladores';
        this.lastSeenLabel = 'No hay dispositivos registrados';
        this.tooltipText = 'Sin controladores registrados';
        break;
    }
  }
}
